import React, { useCallback, useEffect, useMemo, useState } from "react";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import PayableCompanyCard from "../../../components/Payables/PayableCompanyCard";
import PayablesSummaryHero from "../../../components/Payables/PayablesSummaryHero";
import { useNavigate } from "react-router-dom";
import { BsSearch } from "react-icons/bs";
import { FiInbox } from "react-icons/fi";
import { GetPayablesOverviewAPI } from "../../../ApiRequests";
import { ErrorToast } from "../../../utils/ShowToast";
import { getAppBasePath } from "../../../utils/appPaths";
import { usePageRefresh } from "../../../hooks/usePageRefresh";

const SORT_OPTIONS = [
  { id: "due_desc", label: "Highest payable" },
  { id: "due_asc", label: "Lowest payable" },
  { id: "cartons_desc", label: "Most cartons" },
  { id: "name", label: "Name A–Z" },
  { id: "date_desc", label: "Newest first" },
  { id: "date_asc", label: "Oldest first" },
];

const rowTime = (row) => {
  const t = row?.updatedAt || row?.createdAt;
  if (!t) return 0;
  const ms = new Date(t).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const Payables = () => {
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("due_desc");
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [overall, setOverall] = useState(null);
  const navigate = useNavigate();

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetPayablesOverviewAPI();
      const payload = response?.data?.data?.payload;
      setCompanies(Array.isArray(payload?.companies) ? payload.companies : []);
      setOverall(payload?.overall || null);
    } catch (err) {
      ErrorToast(
        err.response?.data?.error?.msg || "Failed to load payables"
      );
      setCompanies([]);
      setOverall(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  usePageRefresh(loadOverview);

  const payableCompanies = useMemo(() => {
    return (companies || []).filter(
      (c) =>
        Number(c.payable) > 0 ||
        Number(c.total) > 0 ||
        Number(c.boxQty) > 0 ||
        Number(c.totalPurchaseQty) > 0
    );
  }, [companies]);

  const summary = useMemo(() => {
    if (overall) {
      return {
        count: overall.companiesWithPayable ?? payableCompanies.filter((c) => Number(c.payable) > 0).length,
        total: overall.total ?? 0,
        paid: overall.paid ?? 0,
        remaining: overall.remaining ?? 0,
        totalCartons: overall.totalCartons ?? 0,
        totalPurchaseQty: overall.totalPurchaseQty ?? 0,
      };
    }
    const withPayable = payableCompanies.filter((c) => Number(c.payable) > 0);
    return {
      count: withPayable.length,
      total: payableCompanies.reduce((s, c) => s + Number(c.total || 0), 0),
      paid: payableCompanies.reduce((s, c) => s + Number(c.paid || 0), 0),
      remaining: payableCompanies.reduce((s, c) => s + Number(c.payable || 0), 0),
      totalCartons: payableCompanies.reduce((s, c) => s + Number(c.boxQty || 0), 0),
      totalPurchaseQty: payableCompanies.reduce(
        (s, c) => s + Number(c.totalPurchaseQty || 0),
        0
      ),
    };
  }, [overall, payableCompanies]);

  const maxDue = useMemo(
    () =>
      payableCompanies.reduce((m, c) => Math.max(m, Number(c.payable || 0)), 0),
    [payableCompanies]
  );

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    let list = payableCompanies;
    if (q) {
      list = list.filter(
        (c) =>
          String(c.name || "").toLowerCase().includes(q) ||
          String(c.contact || "").includes(q) ||
          String(c.address || "").toLowerCase().includes(q) ||
          (c.purchaseItems || []).some((item) =>
            String(item.name || "").toLowerCase().includes(q)
          )
      );
    }
    const sorted = [...list];
    if (sortBy === "due_desc") {
      sorted.sort((a, b) => Number(b.payable || 0) - Number(a.payable || 0));
    } else if (sortBy === "due_asc") {
      sorted.sort((a, b) => Number(a.payable || 0) - Number(b.payable || 0));
    } else if (sortBy === "cartons_desc") {
      sorted.sort((a, b) => Number(b.boxQty || 0) - Number(a.boxQty || 0));
    } else if (sortBy === "date_desc") {
      sorted.sort((a, b) => rowTime(b) - rowTime(a));
    } else if (sortBy === "date_asc") {
      sorted.sort((a, b) => rowTime(a) - rowTime(b));
    } else {
      sorted.sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      );
    }
    return sorted;
  }, [payableCompanies, searchText, sortBy]);

  const base = getAppBasePath();

  return (
    <BodyWrapper>
      <Header
        title="Company Payables"
        desc="Supplier balances, cartons purchased & item qty"
      />

      <PayablesSummaryHero summary={summary} loading={loading} />

      <div className="flex flex-col max980:gap-3 gap-4 mb-5">
        <div className="relative flex-1">
          <BsSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search company, phone, address or item..."
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-line bg-card text-sm shadow-soft focus:outline-none focus:border-main focus:ring-2 focus:ring-main/15 transition-all"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {SORT_OPTIONS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setSortBy(id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                sortBy === id
                  ? "bg-ink text-white border-ink shadow-soft"
                  : "bg-card text-inkSecondary border-line hover:border-ink/40"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {!loading && filtered.length > 0 && (
        <p className="text-xs text-muted font-medium mb-3">
          Showing {filtered.length} of {payableCompanies.length}
          {summary.totalCartons > 0 && (
            <span className="ml-2 text-sky-700">
              · {Number(summary.totalCartons).toLocaleString("en-PK")} cartons
              overall
            </span>
          )}
        </p>
      )}

      {loading ? (
        <FetchingLoading />
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/80">
          <div className="w-16 h-16 rounded-2xl bg-ink/10 flex items-center justify-center mb-4">
            <FiInbox className="text-ink" size={28} />
          </div>
          <p className="font-bold text-ink text-lg">
            {payableCompanies.length === 0 ? "All clear!" : "No matches found"}
          </p>
          <p className="text-sm text-muted mt-1 text-center max-w-xs">
            {payableCompanies.length === 0
              ? "No company purchases or payables found."
              : "Try a different search term or clear filters."}
          </p>
          {searchText && (
            <button
              type="button"
              onClick={() => setSearchText("")}
              className="mt-4 text-sm font-semibold text-ink hover:underline"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3 pb-4">
          {filtered.map((company, index) => (
            <PayableCompanyCard
              key={company._id}
              company={company}
              maxDue={maxDue}
              rank={index + 1}
              onOpen={() => navigate(`${base}/payables/${company._id}`)}
            />
          ))}
        </div>
      )}
    </BodyWrapper>
  );
};

export default Payables;
